'use client';

import { useAuth, useClerk, useOrganization, useOrganizationList } from '@clerk/nextjs';
import { Check, ChevronDown, Plus, Search, Settings } from 'lucide-react';
import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { OrgAvatar, NAVBAR_ACCOUNT_AVATAR_CLASS } from '@/components/layout/account-avatars';
import { ENTERPRISE_SIDEBAR_LABEL_MAX } from '@/components/layout/enterprise-sidebar-dimensions';
import { useRTLUtils } from '@/lib/translation';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuGroup,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { isSuperAdminViewClient } from '@/lib/permissions/client';
import { pinKindwayOrgFirst } from '@/lib/account/pin-kindway-org-in-switcher';
import { cn } from '@/lib/utils';
import {
  getClerkOrganizationProfileMobileAppearance,
  isMobileViewport,
} from '@/lib/auth/clerk-organization-profile-mobile-styles';
import {
  getClerkOrganizationProfileLogoAppearance,
  mergeClerkAppearance,
} from '@/lib/auth/clerk-organization-profile-logo-styles';
import {
  getClerkOrganizationProfileMembersAppearance,
} from '@/lib/auth/clerk-organization-profile-members-styles';

type OrgAccountSwitcherProps = {
  compact?: boolean;
  triggerClassName?: string;
  variant?: 'dropdown' | 'sheet' | 'enterprise-sidebar';
  sheetPart?: 'trigger' | 'panel';
  sheetExpanded?: boolean;
  onSheetToggle?: () => void;
  collapsed?: boolean;
  mobileComfortable?: boolean;
  /** Enterprise sidebar hover-expand guard while the dropdown is open. */
  onPortaledMenuOpenChange?: (open: boolean) => void;
  theme?: 'navbar' | 'enterprise';
  compactSheetHeader?: boolean;
};

const ENTERPRISE_ICON_SLOT = 'flex h-10 w-10 shrink-0 items-center justify-center';

/** Fade-only open: zoom-in scales text and logos on the compositor layer. */
const ORG_SWITCHER_MENU_CONTENT_CLASS =
  'w-72 p-0 data-[state=open]:[--tw-enter-scale:1] data-[state=closed]:[--tw-exit-scale:1]';

/** Clerk defaults to pageSize 10; load larger pages and auto-fetch the rest for the switcher. */
const USER_MEMBERSHIPS_LIST_CONFIG = {
  infinite: true,
  pageSize: 100,
} as const;

/** Show an in-dropdown filter once the switchable org list is long enough to scan. */
const ORG_LIST_SEARCH_THRESHOLD = 5;

function enterpriseRevealLabelClass(collapsed: boolean): string {
  return cn(
    'min-w-0 truncate text-sm font-medium text-emerald-50 transition-[max-width,opacity,padding] duration-150 ease-out',
    collapsed
      ? 'max-w-0 overflow-hidden px-0 opacity-0'
      : cn(ENTERPRISE_SIDEBAR_LABEL_MAX, 'flex-1 px-0.5 opacity-100'),
  );
}

function enterpriseRevealChevronClass(collapsed: boolean, visible: boolean): string {
  return cn(
    'h-4 w-4 shrink-0 text-emerald-200/80 transition-[max-width,opacity] duration-150 ease-out',
    !visible || collapsed ? 'max-w-0 overflow-hidden opacity-0' : 'max-w-4 opacity-100',
  );
}

type Membership = NonNullable<ReturnType<typeof useOrganizationList>['userMemberships']['data']>[number];

function filterMemberships(memberships: Membership[], query: string): Membership[] {
  const normalized = query.trim().toLowerCase();
  if (!normalized) return memberships;
  return memberships.filter((membership) => {
    const name = membership.organization.name.toLowerCase();
    const slug = membership.organization.slug?.toLowerCase() ?? '';
    return name.includes(normalized) || slug.includes(normalized);
  });
}

function OrgSheetActions({ onManageOrganization }: { onManageOrganization: () => void }) {
  const { t } = useTranslation();

  return (
    <div className="px-3 py-2">
      <Button
          type="button"
          variant="outline"
          size="sm"
          className="h-8 w-full gap-1.5 text-xs"
          onClick={onManageOrganization}
        >
        <Settings className="size-3.5" />
        {t('account.manageOrganization')}
        </Button>
    </div>
  );
}

function OrgCurrentHeader({
  organization,
  orgName,
  onManageOrganization,
}: {
  organization: NonNullable<ReturnType<typeof useOrganization>['organization']>;
  orgName: string;
  onManageOrganization: () => void;
}) {
  const { t } = useTranslation();

  return (
    <div className="flex items-center gap-2 p-3">
      <OrgAvatar name={orgName} imageUrl={organization.imageUrl} />
      <div className="min-w-0 flex-1 text-start">
        <p className="truncate text-sm font-semibold">{orgName}</p>
      </div>
      <Button
        type="button"
        variant="outline"
        size="sm"
        className="h-8 shrink-0 gap-1.5 px-2.5 text-xs"
        onClick={onManageOrganization}
      >
        <Settings className="size-3.5" />
        {t('account.manageOrganization')}
      </Button>
    </div>
  );
}

function OrgMembershipRow({
  membership,
  switchingOrgId,
  inline,
  onSelectOrganization,
  theme = 'navbar',
}: {
  membership: Membership;
  switchingOrgId: string | null;
  inline: boolean;
  onSelectOrganization: (organizationId: string) => void;
  theme?: 'navbar' | 'enterprise';
}) {
  const org = membership.organization;
  const isSwitching = switchingOrgId === org.id;

  if (inline) {
    return (
      <button
        type="button"
        disabled={Boolean(switchingOrgId)}
        onClick={() => onSelectOrganization(org.id)}
        className={cn(
          'flex w-full cursor-pointer items-center gap-2 px-3 py-2 text-start text-sm transition-colors disabled:opacity-50',
          theme === 'enterprise'
            ? 'text-emerald-50 hover:bg-emerald-800/50'
            : 'text-gray-700 hover:bg-gray-100/80 dark:text-gray-200 dark:hover:bg-gray-800/50',
        )}
      >
        <OrgAvatar name={org.name} imageUrl={org.imageUrl} size="sm" className="size-7 text-xs" />
        <span className="min-w-0 flex-1 truncate font-medium">{org.name}</span>
        {isSwitching ? <Check className="size-4 shrink-0 animate-pulse opacity-60" /> : null}
      </button>
    );
  }

  return (
    <DropdownMenuItem
      disabled={Boolean(switchingOrgId)}
      onClick={() => onSelectOrganization(org.id)}
      className="gap-2 p-2"
    >
      <OrgAvatar name={org.name} imageUrl={org.imageUrl} size="sm" className="size-7 text-xs" />
      <span className="min-w-0 flex-1 truncate font-medium">{org.name}</span>
      {isSwitching ? <Check className="size-4 shrink-0 animate-pulse opacity-60" /> : null}
    </DropdownMenuItem>
  );
}

function OrgListSearch({
  query,
  onQueryChange,
  theme = 'navbar',
  autoFocus = false,
}: {
  query: string;
  onQueryChange: (value: string) => void;
  theme?: 'navbar' | 'enterprise';
  autoFocus?: boolean;
}) {
  const { t } = useTranslation();
  const isEnterprise = theme === 'enterprise';

  return (
    <div className="px-2 pb-1 pt-1">
      <div className="relative">
        <Search
          className={cn(
            'pointer-events-none absolute start-2 top-1/2 size-3.5 -translate-y-1/2',
            isEnterprise ? 'text-emerald-200/70' : 'text-muted-foreground',
          )}
          aria-hidden
        />
        <Input
          value={query}
          onChange={(event) => onQueryChange(event.target.value)}
          type="search"
          onKeyDown={(event) => event.stopPropagation()}
          placeholder={t('account.searchOrganizations')}
          aria-label={t('account.searchOrganizations')}
          autoFocus={autoFocus}
          autoComplete="off"
          spellCheck={false}
          className={cn(
            'h-8 ps-7 text-xs',
            isEnterprise &&
              'border-emerald-700/60 bg-emerald-950/40 text-emerald-50 placeholder:text-emerald-200/50 focus-visible:ring-emerald-500/40',
          )}
        />
      </div>
    </div>
  );
}

function OrgCreateRow({
  inline,
  onCreateOrganization,
  theme = 'navbar',
}: {
  inline: boolean;
  onCreateOrganization: () => void;
  theme?: 'navbar' | 'enterprise';
}) {
  const { t } = useTranslation();
  const content = (
    <>
      <span className="flex size-6 items-center justify-center rounded-full border border-dashed border-muted-foreground/50">
        <Plus className="size-3.5" />
      </span>
      {t('account.createOrganization')}
    </>
  );

  if (inline) {
    return (
      <button
        type="button"
        onClick={onCreateOrganization}
        className={cn(
          'flex w-full cursor-pointer items-center gap-2 px-3 py-2 text-start text-sm transition-colors',
          theme === 'enterprise'
            ? 'text-emerald-50 hover:bg-emerald-800/50'
            : 'text-gray-700 hover:bg-gray-100/80 dark:text-gray-200 dark:hover:bg-gray-800/50',
        )}
      >
        {content}
      </button>
    );
  }

  return (
    <DropdownMenuItem className="gap-2 p-2" onClick={onCreateOrganization}>
      {content}
    </DropdownMenuItem>
  );
}

function OrgAccountPanel({
  organization,
  orgName,
  otherMemberships,
  switchingOrgId,
  onSelectOrganization,
  onManageOrganization,
  onCreateOrganization,
  canCreateOrganization,
  embedded = false,
  sheetPanel = false,
  theme = 'navbar',
}: {
  organization: ReturnType<typeof useOrganization>['organization'];
  orgName: string;
  otherMemberships: Membership[];
  switchingOrgId: string | null;
  onSelectOrganization: (organizationId: string) => void;
  onManageOrganization: () => void;
  onCreateOrganization: () => void;
  canCreateOrganization: boolean;
  embedded?: boolean;
  sheetPanel?: boolean;
  theme?: 'navbar' | 'enterprise';
}) {
  const { t } = useTranslation();
  const [orgSearchQuery, setOrgSearchQuery] = useState('');
  const orgCount = otherMemberships.length + (organization ? 1 : 0);
  const showSearch = orgCount > ORG_LIST_SEARCH_THRESHOLD;
  const visibleMemberships = showSearch
    ? filterMemberships(otherMemberships, orgSearchQuery)
    : otherMemberships;

  const separator = embedded ? (
    <div
      className={cn(
        'border-t',
        theme === 'enterprise' ? 'border-emerald-800/70' : 'border-gray-200 dark:border-gray-800',
      )}
    />
  ) : (
    <DropdownMenuSeparator />
  );

  return (
    <div className={embedded ? 'flex w-full flex-col gap-1' : undefined}>
      {organization ? (
        sheetPanel ? (
          <OrgSheetActions onManageOrganization={onManageOrganization} />
        ) : (
          <OrgCurrentHeader
            organization={organization}
            orgName={orgName}
            onManageOrganization={onManageOrganization}
          />
        )
      ) : null}

      {organization && otherMemberships.length > 0 ? separator : null}

      {otherMemberships.length > 0 ? (
        <>
          {showSearch ? (
            <OrgListSearch
              query={orgSearchQuery}
              onQueryChange={setOrgSearchQuery}
              theme={embedded ? theme : 'navbar'}
              autoFocus={!embedded}
            />
          ) : null}

          {visibleMemberships.length > 0 ? (
            embedded ? (
              <div className="flex max-h-48 flex-col gap-1 overflow-y-auto py-1">
                {visibleMemberships.map((membership) => (
                  <OrgMembershipRow
                    key={membership.organization.id}
                    membership={membership}
                    switchingOrgId={switchingOrgId}
                    inline
                    onSelectOrganization={onSelectOrganization}
                    theme={theme}
                  />
                ))}
              </div>
            ) : (
              <DropdownMenuGroup className="max-h-48 overflow-y-auto p-1">
                {visibleMemberships.map((membership) => (
                  <OrgMembershipRow
                    key={membership.organization.id}
                    membership={membership}
                    switchingOrgId={switchingOrgId}
                    inline={false}
                    onSelectOrganization={onSelectOrganization}
                    theme={theme}
                  />
                ))}
              </DropdownMenuGroup>
            )
          ) : (
            <p
              className={cn(
                'px-3 py-2 text-xs',
                embedded && theme === 'enterprise'
                  ? 'text-emerald-200/70'
                  : 'text-muted-foreground',
              )}
            >
              {t('account.noMatchingOrganizations')}
            </p>
          )}
        </>
      ) : null}

      {canCreateOrganization ? (
        <>
          {separator}

          {embedded ? (
            <OrgCreateRow inline onCreateOrganization={onCreateOrganization} theme={theme} />
          ) : (
            <DropdownMenuGroup className="p-1">
              <OrgCreateRow inline={false} onCreateOrganization={onCreateOrganization} />
            </DropdownMenuGroup>
          )}
        </>
      ) : null}
    </div>
  );
}

export function OrgAccountSwitcher({
  compact = false,
  triggerClassName,
  variant = 'dropdown',
  sheetPart,
  sheetExpanded = false,
  onSheetToggle,
  collapsed = false,
  mobileComfortable = false,
  onPortaledMenuOpenChange,
  theme = 'navbar',
  compactSheetHeader = false,
}: OrgAccountSwitcherProps) {
  const { t } = useTranslation();
  const { directionClass, textAlignClass } = useRTLUtils();
  const { orgRole, sessionClaims } = useAuth();
  const { openCreateOrganization, openOrganizationProfile } = useClerk();
  const canCreateOrganization = isSuperAdminViewClient(orgRole, sessionClaims);
  const { organization, isLoaded: isOrgLoaded } = useOrganization();
  const { userMemberships, isLoaded: isListLoaded, setActive } = useOrganizationList({
    userMemberships: USER_MEMBERSHIPS_LIST_CONFIG,
  });
  const [switchingOrgId, setSwitchingOrgId] = useState<string | null>(null);

  useEffect(() => {
    if (!isListLoaded || userMemberships.isFetching) return;

    const loadedCount = userMemberships.data?.length ?? 0;
    if (loadedCount >= userMemberships.count || !userMemberships.hasNextPage) return;

    userMemberships.fetchNext();
  }, [
    isListLoaded,
    userMemberships.count,
    userMemberships.data?.length,
    userMemberships.fetchNext,
    userMemberships.hasNextPage,
    userMemberships.isFetching,
  ]);

  const memberships = userMemberships.data ?? [];
  const isLoaded = isOrgLoaded && isListLoaded;
  const orgName = organization?.name ?? t('account.noOrganization');
  const otherMemberships = useMemo(() => {
    const filtered = memberships.filter(
      (membership) => membership.organization.id !== organization?.id,
    );
    return pinKindwayOrgFirst(filtered, canCreateOrganization);
  }, [memberships, organization?.id, canCreateOrganization]);

  async function handleSelectOrganization(organizationId: string) {
    if (!setActive || organizationId === organization?.id || switchingOrgId) return;
    setSwitchingOrgId(organizationId);
    try {
      await setActive({ organization: organizationId });
    } finally {
      setSwitchingOrgId(null);
    }
  }

  const panel = (
    <OrgAccountPanel
      organization={organization}
      orgName={orgName}
      otherMemberships={otherMemberships}
      switchingOrgId={switchingOrgId}
      onSelectOrganization={(organizationId) => void handleSelectOrganization(organizationId)}
      onManageOrganization={() =>
        openOrganizationProfile(
          isMobileViewport()
            ? {
                appearance: mergeClerkAppearance(
                  getClerkOrganizationProfileLogoAppearance(),
                  getClerkOrganizationProfileMembersAppearance(),
                  getClerkOrganizationProfileMobileAppearance(),
                ),
              }
            : {
                appearance: mergeClerkAppearance(
                  getClerkOrganizationProfileLogoAppearance(),
                  getClerkOrganizationProfileMembersAppearance(),
                ),
              },
        )
      }
      onCreateOrganization={() => openCreateOrganization()}
      canCreateOrganization={canCreateOrganization}
      embedded={variant === 'sheet'}
      sheetPanel={variant === 'sheet' && sheetPart === 'panel'}
      theme={theme}
    />
  );

  const rowHeight = mobileComfortable ? 'min-h-11 h-11' : 'min-h-10 h-10';
  const canSwitchOrganization = otherMemberships.length > 0;

  if (!isLoaded) {
    if (variant === 'enterprise-sidebar') {
      return (
        <div
          className={cn(
            'flex w-full min-w-0 items-center gap-0.5',
            rowHeight,
            directionClass,
            collapsed ? 'justify-center' : 'px-0',
          )}
        >
          <span className={ENTERPRISE_ICON_SLOT}>
            <OrgAvatar
              name={null}
              imageUrl={null}
              className="bg-emerald-800/80 text-emerald-100 ring-1 ring-emerald-600/40"
            />
          </span>
          <span className={cn(enterpriseRevealLabelClass(collapsed), textAlignClass)}>
            {t('common.loading')}
          </span>
        </div>
      );
    }

    if (variant === 'sheet' && sheetPart === 'trigger') {
      return (
        <button
          type="button"
          disabled
          className={cn(
            'flex h-9 min-w-0 items-center gap-2 rounded-md px-2 text-sm text-muted-foreground',
            triggerClassName,
          )}
        >
          <OrgAvatar name={null} imageUrl={null} />
          <span className="truncate">{t('common.loading')}</span>
        </button>
      );
    }

    if (variant === 'sheet' && sheetPart === 'panel') {
      return <div className="px-1 py-2 text-sm text-muted-foreground">{t('common.loading')}</div>;
    }

    return (
      <Button
        variant="ghost"
        className={cn('h-9 gap-2 px-2', triggerClassName)}
        disabled
        aria-label={t('common.loading')}
      >
        <OrgAvatar name={null} imageUrl={null} />
        {!compact ? (
          <span className="hidden truncate text-sm font-medium md:inline">{t('common.loading')}</span>
        ) : null}
      </Button>
    );
  }

  if (variant === 'sheet' && sheetPart === 'trigger') {
    const isEnterpriseTheme = theme === 'enterprise';
    return (
      <button
        type="button"
        onClick={onSheetToggle}
        aria-expanded={sheetExpanded}
        aria-label={t('account.switchOrganization')}
        className={cn(
          'flex min-w-0 cursor-pointer touch-manipulation items-center text-start text-sm transition-colors',
          compactSheetHeader
            ? 'h-9 min-h-9 gap-1.5 rounded-md px-1'
            : 'min-h-11 h-11 gap-2 rounded-lg px-2',
          isEnterpriseTheme
            ? 'text-emerald-50 hover:bg-emerald-800/40'
            : 'h-9 text-gray-900 hover:bg-gray-100/80 dark:text-gray-100 dark:hover:bg-gray-800/80',
          sheetExpanded &&
            (isEnterpriseTheme ? 'bg-emerald-800/50' : 'bg-gray-100/80 dark:bg-gray-800/80'),
          triggerClassName,
        )}
      >
        <OrgAvatar
          name={orgName}
          imageUrl={organization?.imageUrl}
          className={isEnterpriseTheme ? 'bg-emerald-800/80 text-emerald-100 ring-1 ring-emerald-600/40' : undefined}
        />
        <span
          className={cn(
            'min-w-0 flex-1 truncate font-medium',
            isEnterpriseTheme ? 'text-emerald-50' : 'text-gray-900 dark:text-gray-100',
          )}
        >
          {orgName}
        </span>
      </button>
    );
  }

  if (variant === 'sheet' && sheetPart === 'panel') {
    return panel;
  }

  if (variant === 'enterprise-sidebar') {
    return (
      <DropdownMenu onOpenChange={onPortaledMenuOpenChange}>
        <DropdownMenuTrigger asChild>
          <button
            type="button"
            className={cn(
              'flex w-full min-w-0 cursor-pointer touch-manipulation items-center gap-0.5 rounded-lg border border-transparent bg-transparent p-0 shadow-none transition-[background-color]',
              rowHeight,
              directionClass,
              collapsed ? 'justify-center' : 'px-0',
              mobileComfortable && 'touch-manipulation',
              'hover:bg-emerald-800/40',
              triggerClassName,
            )}
            disabled={Boolean(switchingOrgId)}
            aria-label={t('account.switchOrganization')}
          >
            <span className={ENTERPRISE_ICON_SLOT}>
              <OrgAvatar
                name={orgName}
                imageUrl={organization?.imageUrl}
                className="bg-emerald-800/80 text-emerald-100 ring-1 ring-emerald-600/40"
              />
            </span>
            <span className={cn(enterpriseRevealLabelClass(collapsed), textAlignClass)}>{orgName}</span>
            <ChevronDown
              className={enterpriseRevealChevronClass(collapsed, canSwitchOrganization)}
              aria-hidden
            />
          </button>
        </DropdownMenuTrigger>
        <DropdownMenuContent
          data-enterprise-account-portal
          className={ORG_SWITCHER_MENU_CONTENT_CLASS}
          align="start"
          side="top"
          sideOffset={4}
        >
          {panel}
        </DropdownMenuContent>
      </DropdownMenu>
    );
  }

  const triggerContent = (
    <>
      <OrgAvatar
        name={orgName}
        imageUrl={organization?.imageUrl}
        className={NAVBAR_ACCOUNT_AVATAR_CLASS}
      />
      {!compact ? (
        <span className="hidden max-w-[9rem] truncate text-sm font-medium md:inline">{orgName}</span>
      ) : null}
    </>
  );

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button
          variant="ghost"
          className={cn(
            'h-9 max-w-full gap-2 px-2 aria-expanded:bg-accent',
            triggerClassName,
          )}
          disabled={Boolean(switchingOrgId)}
          aria-label={t('account.switchOrganization')}
        >
          {triggerContent}
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent className={ORG_SWITCHER_MENU_CONTENT_CLASS} align="end" sideOffset={8}>
        {panel}
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
